/**
 * Add-backs Form Component
 * Displays add-backs and take-backs form for normalizing earnings (SDE calculation)
 */
import React from 'react';
import PropTypes from 'prop-types';

const ADD_BACK_FIELDS = [
  { label: 'Owner Salary', field: 'owner_salary' },
  { label: 'Family Salaries', field: 'family_salaries' },
  { label: 'Salary Adjustments', field: 'salary_adjustments' },
  { label: 'Auto Expenses', field: 'auto_expenses' },
  { label: 'Donations', field: 'donations' },
  { label: 'Insurance Premiums', field: 'insurance_premiums' },
  { label: 'Inventory Adjustments', field: 'inventory_adjustments' },
  { label: 'Meals/Entertainment', field: 'meals_entertainment' },
  { label: 'Personal Expenses', field: 'personal_expenses' },
  { label: 'Phone/Internet', field: 'phone_internet' },
  { label: 'Non-business Services', field: 'non_business_services' },
  { label: 'Travel', field: 'travel' },
  { label: 'One-time Charges', field: 'one_time_charges' },
  { label: 'Retirement', field: 'retirement' },
  { label: 'Supplier Rebates', field: 'supplier_rebates' },
  { label: 'Other', field: 'other_addbacks' }
];

const AddBacksForm = ({
  years,
  financialData,
  formatNumberWithCommas,
  handleInputChange,
  parseNumberFromFormatted
}) => {
  // Calculate total add-backs for a given year
  const calculateAddBacksTotal = (year) => {
    const total = ADD_BACK_FIELDS.reduce((sum, item) => {
      if (financialData[item.field] && financialData[item.field][year] !== undefined) {
        return sum + (parseFloat(parseNumberFromFormatted(financialData[item.field][year])) || 0);
      }
      return sum;
    }, 0);
    return total;
  };

  return (
    <div>
      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h4 className="font-semibold text-green-900 mb-2">💡 What are Add-backs & Take-backs?</h4>
        <p className="text-sm text-green-800">
          <strong>Add-backs</strong> (positive values) are expenses that can be added back to EBITDA because a new owner won't incur them.
          <br />
          <strong>Take-backs</strong> (negative values) are expenses a new owner will incur that aren't currently on the books.
          <br />
          These automatically calculate your SDE (Seller's Discretionary Earnings).
        </p>
      </div>

      <div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left p-3 border">Adjustment Item</th>
              {years.map(y => <th key={y} className="p-3 border text-center">{y}</th>)}
            </tr>
          </thead>
          <tbody>
            {ADD_BACK_FIELDS.map((item, idx) => (
              <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="p-3 border font-medium">{item.label}</td>
                {years.map(year => (
                  <td key={year} className="p-2 border">
                    <input
                      type="text"
                      value={formatNumberWithCommas(financialData[item.field][year])}
                      onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                      className="w-full px-2 py-1 border rounded text-center"
                      placeholder="$0"
                    />
                  </td>
                ))}
              </tr>
            ))}
            {/* Total Row */}
            <tr className="bg-blue-100 font-bold">
              <td className="p-3 border">
                Total Add-backs
                <span className="ml-2 text-xs text-green-600">(Auto)</span>
              </td>
              {years.map(year => {
                const total = calculateAddBacksTotal(year);
                return (
                  <td key={year} className="p-2 border text-center">
                    <span className="text-blue-900 font-bold">
                      {formatNumberWithCommas(total.toFixed(0))}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

AddBacksForm.propTypes = {
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  financialData: PropTypes.object.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  parseNumberFromFormatted: PropTypes.func.isRequired
};

export default AddBacksForm;
